/**
 * Purge de session côté client.
 *
 * Toutes les sorties de session (déconnexion, suppression de compte, token
 * expiré détecté par la NavBar / le MobileShell) doivent passer par ici pour
 * garantir le MÊME état final :
 *   - token supprimé du localStorage,
 *   - store Redux `user` remis à vide,
 *   - `userSync` remis à false dans <App /> via l'évènement ci-dessous.
 *
 * Ce dernier point est indispensable : sans lui, <AppRouters /> reste monté
 * après la navigation vers /login. Comme `setSiteUser({})` laisse un objet
 * VIDE mais TRUTHY dans le store, le portillon d'abonnement d'AppRouters
 * (`siteUser.user && has_subscription !== true`) se déclenchait et affichait
 * la modale bloquante « Choisir un abonnement » par-dessus la page d'accueil
 * — avec « Aucune offre disponible » puisque le token venait d'être effacé.
 */
import { setSiteUser } from 'reduxLib/slices/userSlice'

export const SESSION_CLEARED_EVENT = 'life-session-cleared'

/**
 * Clés localStorage qui ne sont PAS liées au compte : version applicative
 * (cache-buster) et préférence de langue de l'appareil. Elles survivent même
 * à une suppression de compte.
 */
const DEVICE_SCOPED_KEYS = ['life_app', 'i18nExplicitChoice', 'i18nextLng']

/**
 * Supprime toutes les clés localStorage propres à l'utilisateur (avancement
 * du wizard, compteurs, flags d'ouverture de dialogues, messages d'accès
 * refusé…). Utilisé à la suppression de compte pour qu'une inscription
 * ultérieure depuis le même navigateur reparte d'une ardoise vierge.
 */
function purgeUserScopedKeys() {
  try {
    const keys = []
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const key = window.localStorage.key(i)
      if (key && !DEVICE_SCOPED_KEYS.includes(key)) keys.push(key)
    }
    keys.forEach((key) => window.localStorage.removeItem(key))
  } catch (_) {
    // Mode privé / quota : best-effort, le token a déjà été retiré.
  }
}

/**
 * Remet le client dans un état « déconnecté » propre.
 *
 * @param {Function} [dispatch] dispatch Redux (optionnel hors composant).
 * @param {Object}   [options]
 * @param {boolean}  [options.purgeUserData=false] true à la suppression de
 *        compte : purge aussi l'avancement wizard et les flags UI.
 */
export function clearClientSession(dispatch, { purgeUserData = false } = {}) {
  try {
    window.localStorage.removeItem('token')
  } catch (_) {}

  if (purgeUserData) purgeUserScopedKeys()

  if (typeof dispatch === 'function') dispatch(setSiteUser({}))

  try {
    window.dispatchEvent(new CustomEvent(SESSION_CLEARED_EVENT))
  } catch (_) {
    // Environnements sans CustomEvent (SSR) : ignoré.
  }
}

/**
 * Vrai uniquement quand un utilisateur réellement chargé depuis
 * `/api/user/user/` est présent dans le store. `setSiteUser({})` laisse un
 * objet vide truthy : tout test de la forme `if (siteUser.user)` est donc
 * faux positif après une déconnexion. À utiliser partout où l'on conditionne
 * un affichage à « l'utilisateur est chargé ».
 */
export function isUserLoaded(siteUser) {
  const user = siteUser?.user
  if (!user || typeof user !== 'object') return false
  return Boolean(user.id || user.pk || user.email)
}

/** Vrai si un token de session est présent côté client. */
export function hasSessionToken() {
  try {
    return Boolean(window.localStorage.getItem('token'))
  } catch (_) {
    return false
  }
}

/**
 * Routes rendues HORS session : pages d'authentification et contenus publics.
 * Aucun écran applicatif — et surtout aucune modale bloquante (portillon
 * d'abonnement, wizard, crédits…) — ne doit jamais s'afficher par-dessus.
 */
const PUBLIC_PATH_PREFIXES = [
  '/login',
  '/register',
  '/verify',
  '/reset-password',
  '/legal/policies',
]

/**
 * Vrai sur les pages publiques (login, inscription, vérification d'email,
 * réinitialisation de mot de passe, politiques légales).
 *
 * **Why:** garde-fou de dernier recours. Même si un état résiduel traîne dans
 * le store (course entre le démontage d'`<AppRouters />` et une navigation),
 * le portillon d'abonnement ne doit JAMAIS pouvoir se peindre au-dessus de
 * /login — c'est exactement le symptôme vu après une suppression de compte.
 *
 * @param {string} [pathname] par défaut `window.location.pathname`.
 */
export function isPublicAuthPath(pathname) {
  let path = pathname
  if (typeof path !== 'string') {
    try {
      path = window.location.pathname
    } catch (_) {
      return false
    }
  }
  // Comparaison par SEGMENT, jamais par simple préfixe de chaîne :
  // `/registered-assets` commence par `/register` sans être une page
  // publique. On exige donc l'égalité ou un `/` juste après le préfixe.
  const clean = path.replace(/\/+$/, '') || '/'
  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => clean === prefix || clean.startsWith(`${prefix}/`),
  )
}
